import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"


import { Button } from "@/Components/ui/button"
import { Calendar, CalendarDayButton } from "@/Components/ui/calendar"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/Components/ui/popover"
import type { IWebCOOSElementInventory } from "@/services/assets/types"
import { Tooltip, utils, ViewWithLoader } from "@axdspub/axiom-ui-utilities"
import type { Modifiers } from "react-day-picker"
import { Link } from "react-router"
import { useWebCOOSElementInventory } from "@/services/assets/useWebCOOSPostgrest"
import { useAPIContext } from "@/state/ApiContext"
import type { ReactElement } from "react"

const toDayString = (date: Date): string => {
    return format(date, 'yyyy-MM-dd')
}

const toDayStringFromString = (date: string): string => {
    return date.substring(0, 10)
}

export function CalendarDemo({
    selectedDate,
    setSelectedDate,
    inventoryData,
    start,
    end,
    defaultMonth,
    calendarProps,
    dataDomain,
    sizeRange = [25, 45],
    opacityRange = [0.5, 1],
    defaultClassName = "rounded-lg border shadow-sm [--cell-size:--spacing(11)] md:[--cell-size:--spacing(11)]",
    onSelectedDateChange,
    makeLinkFromDay
}: {
    selectedDate: Date | undefined,
    setSelectedDate?: (date: Date | undefined) => void,
    inventoryData?: IWebCOOSElementInventory[],
    start?: Date,
    end?: Date,
    defaultMonth?: Date,
    dataDomain?: [number, number],
    sizeRange?: [number, number],
    opacityRange?: [number, number],
    className?: string,
    defaultClassName?: string,
    calendarProps?: React.ComponentProps<typeof Calendar>,
    onSelectedDateChange?: (date: Date | undefined) => void,
    makeLinkFromDay?: (props: { date: Date, data?: IWebCOOSElementInventory, modifiers: Modifiers }) => string | undefined
}): React.ReactElement {

    const byDate = Object.fromEntries(inventoryData?.map(d => [toDayStringFromString(d.time_bucket), d]) ?? [])
    const dates = Object.keys(byDate).sort() ?? []
    const startMonth = start ?? dates.length ? new Date(dates[0]) : undefined
    const endMonth = end ?? dates.length ? new Date(dates[dates.length - 1]) : undefined
    const domain = dataDomain ?? (inventoryData !== undefined ? [
        Math.min.apply(null, inventoryData.map(d => d.bucket_count)),
        Math.max.apply(null, inventoryData.map(d => d.bucket_count))
    ] : undefined)
    const mergedCalendarProps: React.ComponentProps<typeof Calendar> = {
        ...calendarProps,
        mode: "single",
        className: utils.makeClassName({
            className: calendarProps?.className,
            defaultClassName
        }),
        selected: selectedDate,
        onSelect: (d: Date | undefined) => {
            if (setSelectedDate !== undefined) {
                setSelectedDate(d)
            }
            if (onSelectedDateChange !== undefined) {
                onSelectedDateChange(d)
            }
        },
        captionLayout: calendarProps?.captionLayout ?? "dropdown",
        disabled: calendarProps?.disabled ?? (inventoryData !== undefined
            ? (date) => byDate[toDayString(date)] === undefined || byDate[toDayString(date)].bucket_count === 0
            : undefined
        ),
        startMonth,
        endMonth,
        ...(defaultMonth ? { defaultMonth } : {}),
        components: {
            DayButton: domain !== undefined ? ({ children, modifiers, day, ...props }) => {
                const data = byDate[toDayString(day.date)]
                const noData = data === undefined || data.bucket_count === 0
                const size = domain !== undefined && data !== undefined
                    ? `${sizeRange[0] + (data.bucket_count - domain[0]) / (domain[1] - domain[0]) * (sizeRange[1] - sizeRange[0])}px`
                    : null
                const opacity = domain !== undefined && data !== undefined
                    ? opacityRange[0] + (data.bucket_count - domain[0]) / (domain[1] - domain[0]) * (opacityRange[1] - opacityRange[0])
                    : null
                const button = (
                    <Tooltip className='cursor-pointer' contentClassName="z-60" content={<div className='flex flex-col gap-2'>
                        <p>{format(day.date, 'PPP')}</p>
                        <p>{data?.bucket_count > 0 ? `${data.bucket_count} media files` : 'No media available'}</p>
                        {!noData && <p className='text-xs text-slate-400'><em>Click to see media</em></p>}
                    </div>} dark={true} useSpan={true}>
                        <CalendarDayButton day={day} modifiers={modifiers} {...props} className='cursor-pointer'>

                            {!noData && <>
                                <span className='hidden'>{data.bucket_count}</span>
                                {
                                    size !== null && <span className={`block rounded-4xl absolute z-0 border-2 border-white ${modifiers.outside ? 'bg-slate-300' : 'bg-slate-600'} `} style={{
                                        width: size,
                                        height: size,
                                        opacity: opacity !== null ? opacity : 1,
                                    }}></span>
                                }
                            </>}

                            <span className={`z-5 ${!noData ? 'text-white font-bold' : 'text-slate-900'}`}>{children}</span>

                        </CalendarDayButton>
                    </Tooltip>
                )

                const link = makeLinkFromDay !== undefined
                    ? makeLinkFromDay({ date: day.date, data, modifiers })
                    : undefined

                if (link !== undefined) {
                    return <Link to={link}>{button}</Link>
                }

                return button
            } : undefined,
            ...calendarProps?.components
        }


    }
    return <Calendar
        {...mergedCalendarProps}


    />
}

export function DatePickerDemo({
    selectedDate,
    setSelectedDate,
    inventoryData,
    start,
    end
}: {
    selectedDate?: Date,
    setSelectedDate?: (date: Date | undefined) => void,
    inventoryData?: IWebCOOSElementInventory[],
    start?: Date,
    end?: Date
}): React.ReactElement {
    //const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(date)

    const [openOverride, setOpenOverride] = React.useState<boolean | undefined>(undefined);

    return (
        <Popover open={openOverride} onOpenChange={setOpenOverride}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    data-empty={!selectedDate}
                    className="data-[empty=true]:text-muted-foreground w-[280px] justify-start text-left font-normal"
                >
                    <CalendarIcon />
                    {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
                <CalendarDemo
                    onSelectedDateChange={() => { setOpenOverride?.(false) }}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    inventoryData={inventoryData}
                    start={start}
                    end={end}
                />
            </PopoverContent>
        </Popover>
    )
}

const InventoryLoaderCalendar = ({
    serviceSlug,
    selectedDate,
    setSelectedDate,
    start,
    end
}: {
    serviceSlug: string,
    selectedDate?: Date,
    setSelectedDate?: (date: Date | undefined) => void,
    start?: Date,
    end?: Date
}): ReactElement => {
    const apiContext = useAPIContext()

    const { data, isLoading, isFetching, error } = useWebCOOSElementInventory({
        ...apiContext,
        params: {
            filters: [
                {
                    column: 'service_slug',
                    operator: 'eq',
                    value: serviceSlug
                }
            ],
            order: [
                {
                    column: 'time_bucket',
                    dir: 'desc'
                }
            ]
        },
        grouping: 'day'
    })

    return (
        <ViewWithLoader data={data} isLoading={isLoading} isFetching={isFetching} error={error} keepExistingContentWhileLoading={true}>
            {data && <CalendarDemo
                selectedDate={selectedDate} setSelectedDate={setSelectedDate} inventoryData={data} start={start} end={end} />

            }
        </ViewWithLoader>
    )

}


export function DatePickerLoader({
    serviceSlug,
    selectedDate,
    setSelectedDate,
    start,
    end
}: {
    serviceSlug: string,
    selectedDate?: Date,
    setSelectedDate?: (date: Date | undefined) => void,
    start?: Date,
    end?: Date
}): React.ReactElement {
    //const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(date)



    return (

        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    data-empty={!selectedDate}
                    className="data-[empty=true]:text-muted-foreground w-[280px] justify-start text-left font-normal"
                >
                    <CalendarIcon /> {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
                <InventoryLoaderCalendar
                    serviceSlug={serviceSlug}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    start={start}
                    end={end}
                />
            </PopoverContent>
        </Popover>
    )
}

